import { ComponentPropsWithoutRef, useEffect, useRef, useState } from "react";
import styles from "./GalleryVideoElement.module.scss";
import classNames from "classnames/bind";
import { GalleryVideoObject } from "../../utils";
import { dispatchGalleryMonetateQEvent } from "../Analytics/dispatchGalleryMonetateQEvent";
import React from "react";
import { getTimeUpdateHandler } from "../Analytics/AdobeAnalytics";
import { useGalleryContext } from "../../GridGallery/GridGalleryContext";
import { getBaseUrlAndQuery } from "@/utils/url/getBaseUrlAndQuery";

const cx = classNames.bind(styles);

interface GalleryVideoElementProps extends ComponentPropsWithoutRef<"div"> {
  video: GalleryVideoObject;
  videoId: string;
  isModal?: boolean;
  isInView: boolean;
}

export const GalleryVideoElement = ({
  video,
  videoId,
  isModal = false,
  isInView,
  className,
  ...props
}: GalleryVideoElementProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isCustomPlayButtonVisible, setIsCustomPlayButtonVisible] =
    useState(true);

  const { product, enableAnalytics, selectedVariant } = useGalleryContext() || {
    product: null,
    enableAnalytics: false,
    selectedVariant: null,
  };

  const videoTimeline = React.useRef({
    started: false,
    reached50: false,
    videoCompleted: false,
  });

  const buttonClassNames = cx(styles.videoPlayButton, {
    hideButton: !isCustomPlayButtonVisible,
  });

  useEffect(() => {
    setIsCustomPlayButtonVisible(true);
  }, [video]);

  useEffect(() => {
    if (videoRef.current && !isInView) {
      videoRef.current.pause();
    }
  }, [isInView]);

  const handleVideoClick = () => {
    if (videoRef.current) {
      videoRef.current.play();
    }
    dispatchGalleryMonetateQEvent(
      "VideoInteraction",
      isModal ? "videoModal" : "generic",
    );
  };

  const { baseUrl } = getBaseUrlAndQuery(video.imageUrl);

  return (
    // eslint-disable-next-line
    <div className={cx(styles.videoWrapper, className)} {...props}>
      <button
        data-testid="standard:gallery:video:play-button"
        aria-label="play video"
        className={buttonClassNames}
        tabIndex={isInView ? 0 : -1}
        onClick={handleVideoClick}
      />
      <video
        ref={videoRef}
        className={cx(styles.videoElement, {
          modal: isModal,
        })}
        data-testid="standard:gallery:video"
        src={video.videoUrl}
        controls={!isCustomPlayButtonVisible}
        onClick={handleVideoClick}
        onTimeUpdate={() => {
          if (enableAnalytics) {
            getTimeUpdateHandler(
              "pdp",
              videoRef,
              `video${videoId}`,
              videoTimeline,
              product,
              selectedVariant,
            );
          }
        }}
        onPlay={() => setIsCustomPlayButtonVisible(false)}
        poster={`${baseUrl}?wid=720&hei=640`}
        tabIndex={isInView ? 0 : -1}
      />
    </div>
  );
};
